import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth';
import { EyeIconComponent } from '../../shared/svg/eye-icon';
import { EyeSlashIconComponent } from '../../shared/svg/eye-slash-icon';

@Component({
	selector: 'app-login',
	imports: [CommonModule, FormsModule, EyeIconComponent, EyeSlashIconComponent],
	templateUrl: './login.html',
	styleUrl: './login.scss'
})
export class Login {
	private authService = inject(AuthService);
	private router = inject(Router);

	// Form data
	email = '';
	password = '';

	// UI state
	isLoading = signal<boolean>(false);
	errorMessage = signal<string>('');
	showPassword = signal<boolean>(false);

	// Auth status for debugging
	authStatus = signal<string>('Checking...');
	userEmail = signal<string | null>(null);
	userId = signal<string | null>(null);

	// Signal to track if avatar is clicked (showing surprised state)
	private isSurprised = signal(false);

	// Computed property to determine which avatar image to display
	avatarImage = computed(() => {
		// Show surprised image when clicked
		if (this.isSurprised()) {
			return 'assets/images/avatar/Crustopher-Surprised.png';
		}
		return 'assets/images/avatar/Crustopher-Happy.png';
	});

	constructor() {
		// Subscribe to auth state changes to show current status
		this.authService.authInitialized$.subscribe(() => {
			if (this.authService.isAuthenticated()) {
				const user = this.authService.getCurrentUser();
				this.userId.set(user?.uid || null);
				this.userEmail.set(user?.email || null);
				this.authStatus.set(`Logged in as: ${user?.email || 'Unknown'}`);
			} else {
				this.userId.set(null);
				this.userEmail.set(null);
				this.authStatus.set('Logged out');
			}
		});
	}

	/**
	 * Handle form submission for login
	 */
	async onSubmit(): Promise<void> {
		// Reset error message
		this.errorMessage.set('');

		// Validate form
		if (!this.validateForm()) {
			return;
		}

		this.isLoading.set(true);

		try {
			await this.authService.signIn(this.email, this.password);
			this.router.navigate(['/app/home']);
		} catch (error: any) {
			// handleAuthError returns a string, so if error is already a string, use it
			// Otherwise, extract message from Error object or use default
			const errorMsg = typeof error === 'string' 
				? error 
				: (error?.message || 'An error occurred. Please try again.');
			this.errorMessage.set(errorMsg);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Validate form inputs
	 */
	private validateForm(): boolean {
		if (!this.email.trim()) {
			this.errorMessage.set('Please enter your email address.');
			return false;
		}

		if (!this.isValidEmail(this.email)) {
			this.errorMessage.set('Please enter a valid email address.');
			return false;
		}

		if (!this.password) {
			this.errorMessage.set('Please enter your password.');
			return false;
		}

		return true;
	}

	/**
	 * Validate email format
	 */
	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}

	/**
	 * Toggle password visibility
	 */
	togglePasswordVisibility(): void {
		this.showPassword.set(!this.showPassword());
	}

	/**
	 * Navigate to signup page
	 */
	goToSignup(): void {
		this.router.navigate(['/auth/signup']);
	}

	/**
	 * Navigate to forgot password page
	 */
	goToForgotPassword(): void {
		this.router.navigate(['/auth/forgot-password']);
	}

	/**
	 * Force sign out for testing
	 */
	async forceSignOut(): Promise<void> {
		try {
			await this.authService.signOut();
		} catch (error) {
			console.error('Error signing out:', error);
		}
	}

	// Handle avatar click to show surprised state
	onAvatarClick() {
		this.isSurprised.set(true);
		// Revert back to happy state after 500ms
		setTimeout(() => {
			this.isSurprised.set(false);
		}, 500);
	}
}
